import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "api/api";

// MUI components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDBadge from "components/MDBadge";

// Dashboard layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import DOMPurify from "dompurify"; // sanitize HTML

function FAQs() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const navigate = useNavigate();

  const columns = [
    { Header: "Question", accessor: "question", align: "left", sortable: true },
    { Header: "Answer", accessor: "answer", align: "left", sortable: false },
    { Header: "Date", accessor: "date", align: "left", sortable: false },
    { Header: "Status", accessor: "status", align: "left", sortable: false },
    { Header: "Action", accessor: "action", align: "left", sortable: false },
  ];

  useEffect(() => {
    api
      .get("admin/faq")
      .then((res) => {
        const _faqs = res?.data?.data || [];

        const formatDateTime = (dateString) => {
          if (!dateString) return "N/A";
          const date = new Date(dateString);
          return date.toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
          });
        };

        const truncate = (text, maxLength = 100) => {
          if (!text) return "";
          return text.length > maxLength ? text.slice(0, maxLength) + "..." : text;
        };

        const formattedRows = _faqs.map((q) => ({
          id: q.id,
          question: q.question,
          answer: (
            <div
              dangerouslySetInnerHTML={{
                __html: DOMPurify.sanitize(
                  q.answer.length > 100 ? q.answer.slice(0, 100) + "..." : q.answer
                ),
              }}
            />
          ),
          date: formatDateTime(q.createdAt),
          status: (
            <MDBox ml={-1}>
              <MDBadge
                badgeContent={q.isActive === true ? "Active" : "Inactive"}
                color={q.isActive === true ? "success" : "error"}
                variant="gradient"
                size="sm"
              />
            </MDBox>
          ),
          action: (
            <>
              <a
                href={`/faqs/${q.id}`}
                style={{
                  color: "#1976d2",
                  textDecoration: "none",
                  marginRight: "10px",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                }}
              >
                <EditIcon fontSize="small" />
              </a>
              <a
                onClick={() => handleDelete(q.id)}
                style={{
                  color: "#d32f2f",
                  textDecoration: "none",
                  fontWeight: "500",
                  fontSize: "0.875rem",
                  cursor: "pointer",
                }}
              >
                <DeleteIcon fontSize="small" />
              </a>
            </>
          ),
        }));

        setRows(formattedRows);
        setLoading(false);
      })
      .catch((err) => {
        console.error("API Error:", err);
        setLoading(false);
      });
  }, []);

  const handleDelete = async (faqId) => {
    const confirmDelete = window.confirm("Are you sure you want to delete this FAQ?");
    if (!confirmDelete) return;

    try {
      const response = await api.delete(`admin/faq/delete/${faqId}`);
      console.log("Delete Response:", response.data);

      if (response.status === 200 && response.data.status === true) {
        alert("FAQ deleted successfully!");
        setRows((prevRows) => prevRows.filter((row) => row.id !== faqId));
      } else {
        alert(response.data.message || "Failed to delete the FAQ.");
      }
    } catch (error) {
      console.error("Delete error:", error);
      alert("An error occurred while deleting. Please try again.");
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <MDBox display="flex" justifyContent="space-between" alignItems="center">
                  <MDTypography variant="h6" color="white">
                    FAQs List
                  </MDTypography>
                  <Link to="/faqs/add-faqs" style={{ textDecoration: "none" }}>
                    <MDTypography
                      variant="button"
                      color="white"
                      sx={{
                        backgroundColor: "#ffffff33",
                        padding: "6px 16px",
                        borderRadius: "8px",
                        fontWeight: "bold",
                        cursor: "pointer",
                        transition: "0.3s",
                        "&:hover": {
                          backgroundColor: "#ffffff55",
                        },
                      }}
                    >
                      + Add FAQs
                    </MDTypography>
                  </Link>
                </MDBox>
              </MDBox>
              <MDBox pt={3}>
                {loading ? (
                  <MDTypography px={2}>Loading...</MDTypography>
                ) : (
                  <DataTable
                    table={{ columns, rows }}
                    isSorted={true}
                    entriesPerPage={true}
                    showTotalEntries={true}
                    noEndBorder
                  />
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default FAQs;
